import { useCallback, useEffect, useState, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { SearchBox } from "../../../components/Search";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import {
  getRajasthanGssRestrictedAreaSummaryMetrics,
  getRajasthanGssRestrictedSummaryMetricsDiscom,
  getRajasthanGssRestrictedSummaryMetricsTotal,
  getRajasthanGssLoadMetricsRestrictedDiscomDistrict,
} from "../Utility/RestApi";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { useLegendFilter } from "../../../hooks/useLegendFilter";

// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const GROUP_BY = [
  { label: "Area", key: "area", values: ["area", "discom"] },
  { label: "Discom", key: "discom", values: ["discom"] },
  { label: "District", key: "district", values: ["district", "discom"] },
  { label: "Total", key: "total", values: ["total"] },
];

const DISCOM = [
  { label: "JDVVNL", key: "JDVVNL", apiKey: "JDVVNL" },
  { label: "JVVNL", key: "JVVNL", apiKey: "JVVNL" },
  { label: "AVVNL", key: "AVVNL", apiKey: "AVVNL" },
];

const KNOWN_DISCOMS = DISCOM.map((d) => d.apiKey);

const METRIC_TYPES = [
  { field: "min", displayName: "Min", hasDate: true },
  { field: "max", displayName: "Max", hasDate: true },
  { field: "avg", displayName: "Average", hasDate: false },
  { field: "mode", displayName: "Mode", hasDate: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getApiDiscomKeys(displayKeys: string[]): string[] {
  return displayKeys.map((k) => DISCOM.find((d) => d.key === k)?.apiKey ?? k);
}

function getDisplayDiscomName(apiKey: string): string {
  return DISCOM.find((d) => d.apiKey === apiKey)?.label ?? apiKey;
}

function splitKeyByDiscom(key: string): {
  entityName: string;
  discomName: string;
} {
  for (const discom of KNOWN_DISCOMS) {
    if (key.endsWith(`_${discom}`)) {
      return {
        entityName: key.slice(0, -(discom.length + 1)),
        discomName: getDisplayDiscomName(discom),
      };
    }
  }
  return { entityName: key, discomName: "" };
}

function getConversionFactor(unit: string): number {
  if (unit === "MWh") return 0.25;
  if (unit === "LU") return 0.0025;
  return 1;
}

function buildSlotBase(item: any): object {
  return {
    slot: item.slot,
    slot_number: item.slot,
    slot_with_time: item.time_string
      ? `${item.slot} (${item.time_string})`
      : item.slot,
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RajasthanDrawalSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: any[];
  onGroupByChange: (index: number) => void;
  selectedArea: any[];
  onAreaChange: (v: any) => void;
  selectedDistricts: any[];
  onDistrictsChange: (v: any) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RajasthanDrawalSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedArea,
  onAreaChange,
  selectedDistricts,
  onDistrictsChange,
}: RajasthanDrawalSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [loading, setLoading] = useState(true);
  const [areaSummaryData, setAreaSummaryData] = useState<any[]>([]);
  const [discomSummaryData, setDiscomSummaryData] = useState<any[]>([]);
  const [districtSummaryData, setDistrictSummaryData] = useState<any[]>([]);
  const [totalSummaryData, setTotalSummaryData] = useState<any[]>([]);

  const [availableAreas, setAvailableAreas] = useState<string[]>([]);
  const [availableDistricts, setAvailableDistricts] = useState<string[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchSummaryData = useCallback(async () => {
    if (!DefaultDates?.[activeTab]) return;

    const trendDates = DefaultDates[activeTab]?.defaultTrendDates || [];
    if (trendDates.length === 0) {
      setAreaSummaryData([]);
      setDiscomSummaryData([]);
      setDistrictSummaryData([]);
      setTotalSummaryData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const allDates = trendDates
        .map((dateRange: any) => dateRange.value)
        .filter(Boolean);

      if (allDates.length === 0) return;

      const promises = allDates.map(async (datesStr: string) => {
        const [startDate, endDate] = datesStr.split(",");
        const [areaRes, discomRes, totalRes, districtRes] = await Promise.all([
          getRajasthanGssRestrictedAreaSummaryMetrics(startDate, endDate),
          getRajasthanGssRestrictedSummaryMetricsDiscom(startDate, endDate),
          getRajasthanGssRestrictedSummaryMetricsTotal(startDate, endDate),
          getRajasthanGssLoadMetricsRestrictedDiscomDistrict(startDate, endDate),
        ]);
        return { areaRes, discomRes, totalRes, districtRes };
      });

      const results = await Promise.all(promises);
      
      let allAreaRes: any[] = [];
      let allDiscomRes: any[] = [];
      let allTotalRes: any[] = [];
      let allDistrictRes: any[] = [];

      results.forEach((res) => {
        if (Array.isArray(res.areaRes)) allAreaRes.push(...res.areaRes);
        if (Array.isArray(res.discomRes)) allDiscomRes.push(...res.discomRes);
        if (Array.isArray(res.totalRes)) allTotalRes.push(...res.totalRes);
        if (Array.isArray(res.districtRes)) allDistrictRes.push(...res.districtRes);
      });

      setAreaSummaryData(allAreaRes);
      setDiscomSummaryData(allDiscomRes);
      setTotalSummaryData(allTotalRes);
      setDistrictSummaryData(allDistrictRes);

    } catch (err) {
      console.error("Error fetching summary data:", err);
      setAreaSummaryData([]);
      setDiscomSummaryData([]);
      setTotalSummaryData([]);
      setDistrictSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ─── Process ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const groupKey = selectedGroupKeys[0]?.key ?? "area";
    let currentData: any[] = [];
    if (groupKey === "area") currentData = areaSummaryData;
    else if (groupKey === "discom") currentData = discomSummaryData;
    else if (groupKey === "total") currentData = totalSummaryData;
    else if (groupKey === "district") currentData = districtSummaryData;

    if (currentData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const factor = getConversionFactor(selectedUnit);
    const isTotal = groupKey === "total";
    const isDiscom = groupKey === "discom";
    const isArea = groupKey === "area";
    const isDistrict = groupKey === "district";

    let filtered = [...currentData];

    // ── Discom filter (not for total) ───────────────────────────────────────
    if (!isTotal && selectedDiscom.length > 0) {
      const apiKeys = getApiDiscomKeys(selectedDiscom);
      filtered = filtered.filter((item) => apiKeys.includes(item.discom));
    }

    // ── Block filter ────────────────────────────────────────────────────────
    if (selectedBlock.length > 0) {
      const blockValues = selectedBlock.map((opt) => opt.value);
      filtered = filtered.filter((item) => blockValues.includes(item.slot));
    }

    // ── District / Area filters (not for total or discom) ──────────────────
    if (!isTotal && !isDiscom) {
      // Build available districts
      const districtLabels = Array.from(
        new Set(
          filtered.map((item) => {
            const name = item.district || item.area;
            const discom = getDisplayDiscomName(item.discom);
            return name && discom && !discom.includes("undefined")
              ? `${name} (${discom})`
              : "";
          }),
        ),
      )
        .filter(Boolean)
        .sort();
      setAvailableDistricts(districtLabels);

      // Apply district filter
      if (selectedDistricts.length > 0) {
        const distValues = selectedDistricts.map((d: any) => d.value);
        filtered = filtered.filter((item) => {
          const name = item.district || item.area;
          const discom = getDisplayDiscomName(item.discom);
          return distValues.includes(`${name} (${discom})`);
        });
      }

      // Build available areas
      const areaLabels = Array.from(
        new Set(
          filtered.map((item) => {
            const name = item.station || item.area;
            const discom = getDisplayDiscomName(item.discom);
            return name && discom && !discom.includes("undefined")
              ? `${name} (${discom})`
              : "";
          }),
        ),
      )
        .filter(Boolean)
        .sort();
      setAvailableAreas(areaLabels);

      // Apply area filter
      if (selectedArea.length > 0) {
        const areaValues = selectedArea.map((a: any) => a.value);
        filtered = filtered.filter((item) => {
          const name = item.station || item.area;
          const discom = getDisplayDiscomName(item.discom);
          return areaValues.includes(`${name} (${discom})`);
        });
      }
    }

    if (filtered.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    const isAreaActive =
      selectedArea.length > 0 ||
      isArea ||
      (!isTotal && !isDiscom && !isDistrict);
    const isDistrictActive =
      !isAreaActive && (selectedDistricts.length > 0 || isDistrict);

    // ── Area / District grouping ────────────────────────────────────────────
    if (isAreaActive || isDistrictActive) {
      const slotMap = new Map<number, any>();

      filtered.forEach((item) => {
        const slot = item.slot;
        const entityName = isAreaActive
          ? item.station || item.area
          : item.district;
        const key = `${entityName || "UNKNOWN"}_${item.discom}`.replace(
          / /g,
          "_",
        );
        const dateOfMin = item.date_of_min_load ?? item.date_of_min;
        const dateOfMax = item.date_of_max_load ?? item.date_of_max;

        if (!slotMap.has(slot)) slotMap.set(slot, buildSlotBase(item));

        const row = slotMap.get(slot);
        row[`${key}_min_load`] = item.min_load * factor;
        row[`${key}_max_load`] = item.max_load * factor;
        row[`${key}_avg_load`] = item.avg_load * factor;
        row[`${key}_mode_load`] = item.mode_load * factor;
        row[`${key}_date_of_min`] = dateOfMin;
        row[`${key}_date_of_min_load`] = dateOfMin;
        row[`${key}_date_of_max`] = dateOfMax;
        row[`${key}_date_of_max_load`] = dateOfMax;
      });

      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(
        Array.from(
          new Set(
            filtered.map((item) => {
              const name = isAreaActive
                ? item.station || item.area
                : item.district;
              return `${name || "UNKNOWN"}_${item.discom}`;
            }),
          ),
        ).sort(),
      );

      // ── Discom grouping ─────────────────────────────────────────────────────
    } else if (isDiscom) {
      const slotMap = new Map<number, any>();

      filtered.forEach((item) => {
        const slot = item.slot;
        const cleanKey = item.discom.replace(/ /g, "_");
        const dateOfMin = item.date_of_min_load ?? item.date_of_min;
        const dateOfMax = item.date_of_max_load ?? item.date_of_max;

        if (!slotMap.has(slot)) slotMap.set(slot, buildSlotBase(item));

        const row = slotMap.get(slot);
        row[`${cleanKey}_min_load`] = item.min_load * factor;
        row[`${cleanKey}_max_load`] = item.max_load * factor;
        row[`${cleanKey}_avg_load`] = item.avg_load * factor;
        row[`${cleanKey}_mode_load`] = item.mode_load * factor;
        row[`${cleanKey}_date_of_min`] = dateOfMin;
        row[`${cleanKey}_date_of_min_load`] = dateOfMin;
        row[`${cleanKey}_date_of_max`] = dateOfMax;
        row[`${cleanKey}_date_of_max_load`] = dateOfMax;
      });

      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(
        Array.from(new Set(filtered.map((item) => item.discom))).sort(),
      );

      // ── Total grouping ──────────────────────────────────────────────────────
    } else if (isTotal) {
      const slotMap = new Map<number, any>();

      filtered.forEach((item) => {
        const slot = item.slot;
        if (slotMap.has(slot)) return;

        const dateOfMin = item.date_of_min_load ?? item.date_of_min;
        const dateOfMax = item.date_of_max_load ?? item.date_of_max;

        slotMap.set(slot, {
          ...buildSlotBase(item),
          total_min_load: item.min_load * factor,
          total_max_load: item.max_load * factor,
          total_avg_load: item.avg_load * factor,
          total_mode_load: item.mode_load * factor,
          total_date_of_min: dateOfMin,
          total_date_of_min_load: dateOfMin,
          total_date_of_max: dateOfMax,
          total_date_of_max_load: dateOfMax,
        });
      });

      const processed = Array.from(slotMap.values()).sort(
        (a, b) => a.slot - b.slot,
      );
      setChartData(processed);
      setTableData(processed);
      setChartKeys(["total"]);
    }
  }, [
    areaSummaryData,
    discomSummaryData,
    districtSummaryData,
    totalSummaryData,
    selectedUnit,
    selectedDiscom,
    selectedGroupKeys,
    selectedArea,
    selectedDistricts,
    selectedBlock,
  ]);

  // ─── Legends ─────────────────────────────────────────────────────────────────

  const legends = useMemo(() => {
    const groupKey = selectedGroupKeys[0]?.key;

    return (chartKeys || [])
      .filter((key) => key?.trim())
      .flatMap((key) => {
        let baseName = "";

        if (groupKey === "total") {
          baseName = "Total Demand";
        } else if (groupKey === "discom") {
          baseName = getDisplayDiscomName(key);
        } else if (groupKey === "area" || groupKey === "district") {
          if (key.includes("_")) {
            const { entityName, discomName } = splitKeyByDiscom(key);
            baseName =
              entityName.toLowerCase() === "rajasthan"
                ? discomName
                : `${entityName} (${discomName})`;
          } else {
            baseName = getDisplayDiscomName(key);
          }
        } else {
          baseName = key;
        }

        const cleanKey = key.replace(/ /g, "_");

        return METRIC_TYPES.map((metric) => ({
          dataKey: `${key}_${metric.field}_load`,
          name: `${baseName} ${metric.displayName}`,
          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,
          stackId:
            selectedChartType.stackId !== undefined ? "summary" : undefined,
          dateKey: metric.hasDate
            ? `${cleanKey}_date_of_${metric.field}`
            : undefined,
        }));
      });
  }, [chartKeys, selectedGroupKeys, selectedChartType]);

  // ─── Tooltip ──────────────────────────────────────────────────────────────────

  const customTooltipFormatter = useCallback(
    (params: any) => {
      const items: any[] = Array.isArray(params) ? params : [params];
      const param = items[0];
      const dataPoint = chartData[param.dataIndex];
      const title = `${getTimeRange(parseInt(param.name))} (${param.name})`;

      const tooltipItems = [...items]
        .sort((a, b) => {
          const order = legends.map((l: any) => l.name);
          const ai = order.indexOf(a.seriesName);
          const bi = order.indexOf(b.seriesName);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        })
        .map((item) => {
          const value = parseFloat(item.value)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          const dataKey =
            legends.find((l) => l.name === item.seriesName)?.dataKey ??
            item.dataKey ??
            "";
          const isMin = dataKey.endsWith("_min_load");
          const isMax = dataKey.endsWith("_max_load");
          const keyPrefix = dataKey
            .replace(/_min_load$/, "")
            .replace(/_max_load$/, "")
            .replace(/_avg_load$/, "")
            .replace(/_mode_load$/, "");

          const dateOfMin =
            dataPoint?.[`${keyPrefix}_date_of_min`] ??
            dataPoint?.[`${keyPrefix}_date_of_min_load`];
          const dateOfMax =
            dataPoint?.[`${keyPrefix}_date_of_max`] ??
            dataPoint?.[`${keyPrefix}_date_of_max_load`];

          let displayName = item.seriesName;
          if (isMin && dateOfMin)
            displayName = `${item.seriesName} (${dateOfMin})`;
          if (isMax && dateOfMax)
            displayName = `${item.seriesName} (${dateOfMax})`;

          return `
            <div class="tooltip-item">
              <div class="color-indicator" style="background:${item.color}"></div>
              <div class="series-name">${displayName}</div>
              <div class="value">${value} ${selectedUnit}</div>
            </div>`;
        });

      return `
        <div class="enhanced-tooltip">
          <div class="tooltip-header"><div class="tooltip-title">${title}</div></div>
          <div class="tooltip-content">${tooltipItems.join("")}</div>
        </div>
        <style>
          .enhanced-tooltip { font-family:'Inter',system-ui,sans-serif; min-width:200px; }
          .tooltip-header   { border-bottom:1px solid #eaeaea; padding-bottom:8px; margin-bottom:8px; }
          .tooltip-title    { font-weight:600; font-size:13px; color:#333; }
          .tooltip-content  { max-height:300px; overflow-y:auto; padding-right:10px; }
          .tooltip-item     { display:flex; align-items:center; gap:8px; padding:4px 0; border-bottom:1px solid #f0f0f0; }
          .tooltip-item:last-child { border-bottom:none; }
          .color-indicator  { width:10px; height:10px; flex-shrink:0; border-radius:50%; }
          .series-name      { font-weight:500; font-size:12px; color:#333; flex:1; }
          .value            { font-weight:600; font-size:12px; color:#333; }
        </style>`;
    },
    [chartData, selectedUnit, legends],
  );

  // ─── Table params ─────────────────────────────────────────────────────────────

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Summary",
      dates: DefaultDates?.[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((i) => i.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((i) => i.label)}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((i) => i.label)}
            selectedItems={selectedGroupKeys.map((i) => i.label)}
            onSelect={(item, index) => {
              onGroupByChange(index);
              if (GROUP_BY[index].key !== "station") onAreaChange([]);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((i) => i.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />

        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Restricted Demand Trend Summary" subtitle="Fetching Chhattisgarh restricted demand trend summary data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              Restricted Demand Summary ({selectedUnit})
            </h2>

            <div className="flex gap-x-6 justify-end">
              {selectedGroupKeys[0]?.key === "district" && (
                <div className="min-w-40">
                  <SearchBox
                    options={availableDistricts.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMany={true}
                    placeholder="Search District"
                    selectedOptions={selectedDistricts}
                    onChange={(v: any) => {
                      onDistrictsChange(v);
                      onAreaChange([]);
                    }}
                  />
                </div>
              )}

              {(selectedGroupKeys[0]?.key === "area" ||
                (selectedGroupKeys[0]?.key === "district" &&
                  selectedDistricts.length > 0)) && (
                <div className="min-w-40">
                  <SearchBox
                    options={availableAreas.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMany={true}
                    placeholder="Search Area"
                    selectedOptions={selectedArea}
                    onChange={(v: any) => onAreaChange(v)}
                  />
                </div>
              )}

              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />

              <button
                onClick={() => {
                  if (dataTab.label === "Summary") downloadSummaryCSV(tablePar);
                  else downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {chartData.length === 0 && !loading && (
            <div
              style={{ padding: "40px", textAlign: "center", color: "#666" }}
            >
              No data available for the selected Dates.
            </div>
          )}

          {dataTab.label === "Chart" && chartData.length > 0 && (
            <ReMixChart
              key={`${selectedChartType.value}-${selectedUnit}`}
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey="slot"
              secondXDataKey={undefined}
              customTooltip={customTooltipFormatter}
            />
          )}

          {dataTab.label === "Summary" && chartData.length > 0 && (
            <LegendSummaryTable {...tablePar} />
          )}

          {dataTab.label === "Table" && chartData.length > 0 && (
            <ChartTable {...tablePar} />
          )}
          </>)}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>GSS Report (RUVITL)</p>}
              text="1. Sum of all GSS load = Actual drawal (Restricted demand)
2. MW = Represents the average (MW) when the selected time interval is Date or Month."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
